import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Paper,
  Box
} from '@mui/material';
import { useTheme } from '@mui/material';
import { getContrastText } from '../../utils/colorUtils';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { BASIC_PLAN, PRO_PLAN } from '../../config/planLimits';

const planFeatures = {
  basic: [
    // Limites de cadastro (BÁSICO)
    { text: `Até ${BASIC_PLAN.limits.maxHabits} hábitos`, available: true },
    { text: `Até ${BASIC_PLAN.limits.maxGoals} metas`, available: true },
    { text: `Até ${BASIC_PLAN.limits.maxChallenges} desafio`, available: true },
    { text: `Até ${BASIC_PLAN.limits.maxNotes} notas`, available: true },
    { text: `Até ${BASIC_PLAN.limits.maxEvents} eventos`, available: true },

    

    // Player e Pomodoro (separados)
    { text: `Player: até ${BASIC_PLAN.limits.maxVideos} áudios/vídeos`, available: true },
    { text: 'Timer Pomodoro', available: true },

    // Estatísticas
    { text: 'Estatísticas básicas', available: true },
    

    // Outras abas
    { text: 'Acesso à aba Dicas de Livros', available: true },
    { text: 'Personalização de cores e sons do app', available: true },
    { text: 'Personalização de layout do calendário', available: true }
  ],
  pro: [
    // Limites de cadastro (PRO)
    { text: 'Hábitos ILIMITADOS', available: true },
    { text: 'Metas ILIMITADAS', available: true },
    { text: 'Desafios ILIMITADOS', available: true },
    { text: 'Notas ILIMITADAS', available: true },
    { text: 'Eventos ILIMITADOS', available: true },

    
    // Player e Pomodoro (separados)
    { text: `Player: áudios/vídeos ILIMITADOS`, available: true },
    { text: 'Timer Pomodoro', available: true },

    // Estatísticas
    { text: 'Estatísticas avançadas', available: true },

    // Outras abas
    { text: 'Acesso à aba Dicas de Livros', available: true },
    { text: 'Personalização de cores e sons', available: true },
    { text: 'Personalização de layout do calendário', available: true }
  ]
};

export default function PlanComparisonDialog({ open, onClose, customColors, title = "Limite do Plano Básico Atingido" }) {
  const theme = useTheme();
  const primaryColor = customColors?.primary || theme.palette.primary.main;
  const primaryTextColor = getContrastText(primaryColor);
  const upgradeUrl = 'https://pay.hotmart.com/M98189010Q?off=ozfmccvn&checkoutMode=10&src=frmt';

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        bgcolor: primaryColor,
        color: primaryTextColor,
        py: 2
      }}>
        {title}
      </DialogTitle>
      <DialogContent sx={{ py: 4 }}>
        <Grid container spacing={4}>
          {/* Coluna do Plano Básico */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              height: '100%', 
              position: 'relative',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white',
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                textAlign: 'center',
                color: primaryColor,
                fontWeight: 'bold',
                mb: 3
              }}>
                Plano Básico
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planFeatures.basic.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {feature.available === false ? (
                      <CloseIcon color="error" />
                    ) : (
                      <CheckIcon sx={{ color: primaryColor }} />
                    )}
                    <Typography color="text.primary">
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Coluna do Plano PRO */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ 
              p: 3, 
              height: '100%',
              position: 'relative',
              bgcolor: primaryColor,
              color: primaryTextColor,
              '& .MuiTypography-root': {
                color: primaryTextColor
              }
            }}>
              <Typography variant="h6" gutterBottom sx={{ 
                textAlign: 'center',
                fontWeight: 'bold',
                mb: 3
              }}>
                Plano PRO
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {planFeatures.pro.map((feature, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckIcon sx={{ color: primaryTextColor }} />
                    <Typography>
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ 
        p: 3, 
        justifyContent: 'center',
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white'
      }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{ 
            mr: 2,
            borderColor: primaryColor,
            color: primaryColor,
            '&:hover': {
              borderColor: primaryColor,
              bgcolor: `${primaryColor}10`
            }
          }}
        >
          Continuar no Plano Básico
        </Button>
        <Button 
          variant="contained"
          component="a"
          href={upgradeUrl}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ 
            bgcolor: primaryColor,
            color: primaryTextColor,
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark'
                ? `${primaryColor}dd`
                : `${primaryColor}bb`
            }
          }}
        >
          Fazer Upgrade para PRO
        </Button>
      </DialogActions>
    </Dialog>
  );
} 